'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@ps/ui/utils';

import {
  useUpdateVideoRef,
  useVideoClipsContext,
  useVideoPlayerContext,
} from '../contexts/video-player';
import type { AnyVideo } from '../functions';

type VideoPlayerProps = {
  video: AnyVideo;
  shouldPlay: boolean;
  muted: boolean;
  loop?: boolean;
  className?: string;
  onLoad?: () => void;
};

export function VideoPlayer({
  video,
  shouldPlay,
  muted,
  loop = true,
  className,
  onLoad,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { positionMs$, currentTime$, totalDuration$ } = useVideoPlayerContext();
  const { autoAdvance, onNext } = useVideoClipsContext();
  const [loading, setLoading] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const hasAutoAdvanced = useRef(false);

  useUpdateVideoRef(videoRef);

  // Handle play/pause via effect (web requires explicit calls)
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (shouldPlay) {
      el.play().catch(() => {
        // Autoplay may be blocked - try muted
        el.muted = true;
        el.play().catch(() => {});
      });
    } else {
      el.pause();
    }
  }, [shouldPlay]);

  // Update muted state
  useEffect(() => {
    const el = videoRef.current;
    if (el) el.muted = muted;
  }, [muted]);

  // Pause on cleanup — the element keeps playing audio otherwise, whether it is
  // truly unmounted (clip modal closing, PS-931 #421) or only frozen by React
  // `<Activity>` (cleanups run but the DOM stays hidden, e.g. minimizing an
  // unmuted welcome video behind the muted mini player). Pause alone silences
  // both; do NOT `removeAttribute('src') + load()` — reloading a frozen Activity
  // instance desyncs its muted state and audio resumes on the hidden element.
  useEffect(() => {
    const el = videoRef.current;
    return () => {
      if (!el) return;
      el.pause();
    };
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    const ms = el.currentTime * 1000;
    positionMs$.next(ms);
    currentTime$.next(el.currentTime);
    totalDuration$.next(el.duration || 0);
  }, [positionMs$, currentTime$, totalDuration$]);

  const handleLoadedData = useCallback(() => {
    setLoading(false);
    const el = videoRef.current;
    if (el) {
      totalDuration$.next(el.duration || 0);
      setIsLandscape(el.videoWidth > el.videoHeight);
    }
    onLoad?.();

    // View tracking is handled in VideoContent.onLoad to avoid double-counting
  }, [totalDuration$, onLoad, video]);

  const handleLoadStart = useCallback(() => {
    setLoading(true);
  }, []);

  const handleEnded = useCallback(() => {
    if (autoAdvance && !hasAutoAdvanced.current) {
      hasAutoAdvanced.current = true;
      if (!onNext?.()) {
        // No next video, loop current
        const el = videoRef.current;
        if (el) {
          el.currentTime = 0;
          el.play().catch(() => {});
        }
      }
    }
  }, [autoAdvance, onNext]);

  const handleError = useCallback(() => {
    setLoading(false);
    if (autoAdvance) {
      hasAutoAdvanced.current = true;
      onNext?.();
    }
  }, [autoAdvance, onNext]);

  const thumbnailUrl =
    video.thumbnailURL ?? video.videoCoverUrl ?? video.gifURL;

  return (
    <div className={cn('relative h-full w-full', className)}>
      {/* Blurred background for landscape videos */}
      {isLandscape && thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
          style={{ filter: 'blur(40px)', transform: 'scale(1.2)' }}
        />
      )}
      <video
        ref={videoRef}
        src={video.videoURL || ''}
        className={cn(
          'absolute inset-0 h-full w-full',
          isLandscape ? 'object-contain' : 'object-cover',
        )}
        playsInline
        loop={loop && !autoAdvance}
        muted={muted}
        preload="auto"
        onTimeUpdate={handleTimeUpdate}
        onLoadedData={handleLoadedData}
        onLoadStart={handleLoadStart}
        onEnded={handleEnded}
        onError={handleError}
      />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          {/* `#C8FF00` is a lone chartreuse accent — it appears exactly once in
              the repo and is NOT the brand mint (#7DFFB3), so mapping it to
              `border-t-brand` would change the colour rather than theme it. The
              player is pinned dark (`VideoModal`, `VideoOverlay`), so a literal
              renders correctly here. Kept deliberately, pending a design call on
              whether this accent should exist at all. */}
          <div className="border-raise/30 theme-keep h-10 w-10 animate-spin rounded-full border-2 border-t-[#C8FF00]" />
        </div>
      )}
    </div>
  );
}
